import { useCallback, useState } from 'react';
import { ConfigProvider, Switch, message } from 'antd';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode } from '@fortawesome/pro-regular-svg-icons/faQrcode';
import { getLang } from 'shared/store/selectors/lang.selector';
import { EVENTS, SCHEDULE_EVENT_TYPES, colorPalette } from 'shared/constants';
import { useManualCheckIn } from 'shared/hooks/attendance.hooks';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { Button } from 'src/components/design-system';
import { useEducationalEventsContext } from '../../educational-events-context';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import {
  getCheckInSchedule,
  getEventStartDayOffset,
  isStaticQr,
} from '../../helpers/event-view.helpers';
import { ConfirmFutureCheckInModal } from '../check-in/confirm-future-check-in-modal';
import { DownloadQrButton } from '../check-in/download-qr-button';
import styles from './event-detail-panel.module.scss';

const SWITCH_THEME = {
  components: {
    Switch: {
      colorPrimary: colorPalette.greenPositive,
      colorPrimaryHover: colorPalette.greenPositive,
    },
  },
};

export function CheckInToggleRow({
  event,
  session,
  isOpen,
  pending,
  open,
  close,
  canManageCheckIn,
  checkedInItems,
  onShowQr,
}) {
  const { lang, userId, users } = useEducationalEventsContext();
  const checkInActiveLang = useSelector(getLang('CHECK_IN_ACTIVE'));
  const { formatOrgDayWithWeekday, formatTimeOrDateTime, orgTimezone } =
    useEducationalEventFormat();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isOrgUser = users.some(user => user.id === userId);
  const startDayOffset = getEventStartDayOffset({ event, timezone: orgTimezone });

  const performOpen = useCallback(async () => {
    const result = await open();
    if (result.ok) {
      analyticsService.track(EVENTS.SCHEDULE.START_CHECK_IN, {
        type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
        eventId: event.id,
        eventTitle: event.title,
      });
    } else {
      message.error(checkInActiveLang.OPEN_FAILED);
    }
  }, [open, checkInActiveLang, event.id, event.title]);

  const handleToggle = useCallback(async () => {
    if (isOpen) {
      onShowQr(false);
      const result = await close();
      if (result.ok) {
        analyticsService.track(EVENTS.SCHEDULE.CLOSE_CHECK_IN, {
          type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
          eventId: event.id,
          eventTitle: event.title,
        });
      } else {
        message.error(checkInActiveLang.CLOSE_FAILED);
      }
    } else if (startDayOffset > 0) {
      setConfirmOpen(true);
    } else {
      await performOpen();
    }
  }, [
    isOpen,
    close,
    onShowQr,
    checkInActiveLang,
    event.id,
    event.title,
    startDayOffset,
    performOpen,
  ]);

  const { opensAt, closesAt } = getCheckInSchedule(event);
  const getHint = () => {
    if (!isOpen) {
      return opensAt
        ? lang.CHECK_IN_OPENS_AT.replace('{time}', formatTimeOrDateTime(opensAt))
        : lang.START_CHECK_IN_HINT;
    }
    return closesAt
      ? lang.CHECK_IN_CLOSES_AT.replace('{time}', formatTimeOrDateTime(closesAt))
      : lang.CHECK_IN_OPEN_UNTIL_CLOSED;
  };
  const hint = getHint();

  const isSelfCheckedIn = checkedInItems.some(item => item.userId === userId);
  const selfCheckIn = useManualCheckIn(event.id);

  const handleSelfCheckIn = async () => {
    try {
      const { successIds } = await selfCheckIn.mutateAsync({ userIds: [userId] });
      if (!successIds.includes(userId)) throw new Error('Self check-in was not registered');
      analyticsService.track(EVENTS.SCHEDULE.SELF_CHECK_IN, {
        type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
        eventId: event.id,
        eventTitle: event.title,
      });
    } catch (error) {
      monitorService.error('Self check-in failed', { error, eventId: event.id, userId });
      message.error(lang.SELF_CHECK_IN_FAILED);
    }
  };

  return (
    <div className={styles.checkInRow}>
      <ConfigProvider theme={SWITCH_THEME}>
        <Switch
          aria-label={lang.CHECK_IN_TOGGLE_LABEL}
          checked={isOpen}
          loading={pending}
          disabled={!canManageCheckIn || pending}
          onChange={handleToggle}
        />
      </ConfigProvider>
      <div className={styles.checkInText}>
        <span className={styles.checkInTitle}>
          {isOpen ? lang.SESSION_OPEN : lang.START_CHECK_IN}
        </span>
        {hint && <span className={styles.checkInHint}>{hint}</span>}
      </div>
      {isStaticQr(event) && (
        <DownloadQrButton size="small" eventId={event.id} eventTitle={event.title} />
      )}
      {isOpen && (
        <div className={styles.checkInActions}>
          {isOrgUser && (
            <Button
              size="small"
              type="default"
              disabled={isSelfCheckedIn || selfCheckIn.isPending}
              loading={selfCheckIn.isPending}
              onClick={handleSelfCheckIn}>
              {isSelfCheckedIn ? lang.CHECK_IN_SELF_DONE : lang.CHECK_YOURSELF_IN}
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            className={styles.showQrButton}
            disabled={!session}
            onClick={() => {
              analyticsService.track(EVENTS.SCHEDULE.SHOW_QR, {
                type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
                eventId: event.id,
                eventTitle: event.title,
              });
              onShowQr(true);
            }}>
            <FontAwesomeIcon icon={faQrcode} />
            {lang.SHOW_QR}
          </Button>
        </div>
      )}
      <ConfirmFutureCheckInModal
        open={confirmOpen}
        daysFromNow={startDayOffset}
        eventDate={formatOrgDayWithWeekday(event.startTime)}
        loading={pending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await performOpen();
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}
